<template>
  <div class="form-item form-calendar span-1">
    <label class="calendar-label">{{ label }}</label>

    <div
        ref="dropdown"
        class="dropdown dropdown-calendar"
        :class="{ 'dropdown-active': isOpen }"
    >
      <button
          ref="dropdownButton"
          class="dropdown__button"
          :class="{ 'dropdown__button--active': isOpen, 'has-action': buttonText !== placeholder }"
          type="button"
          :aria-expanded="isOpen.toString()"
          @click="toggleDropdown"
      >
        {{ buttonText }}
      </button>

      <div
          ref="dropdownList"
          class="dropdown__list"
          :class="{ 'dropdown__list--visible': isOpen }"
      >
        <ul ref="dropdownInner">
          <li
              v-for="option in options"
              :key="option.value"
              class="dropdown__list-item"
          >
            <label class="calendar-label">{{ option.label }}</label>
            <div class="calendar-field">
              <Datepicker
                  v-model="dateValues[option.value]"
                  class="calendar-input"
                  :placeholder="placeholder"
                  :enable-time-picker="false"
                  auto-apply
                  format="dd.MM.yyyy"
                  locale="ru"
                  teleport="body"
                  @update:model-value="updateDate(option.value, $event)"
              />
              <span class="icon-calendar"></span>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div v-if="selectedDates.length" class="labels-group filter-labels">
      <div
          v-for="dateInfo in selectedDates"
          :key="dateInfo.value"
          class="label"
      >
        <span class="font-13">{{ dateInfo.label }}: {{ formatDate(dateInfo.date) }}</span>
        <span class="label-remove" @click="removeDate(dateInfo.value)">
          <svg class="svg-cross">
            <use href="/img/sprite.svg#svg-cross"></use>
          </svg>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { useI18n } from 'vue-i18n';

export default {
  name: 'MultiSelectDateFilter',
  components: {
    Datepicker,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => ({}),
    },
    options: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: 'Выбрать',
    },
  },
  emits: ['update:modelValue'],
  data: () => ({
    isOpen: false,
    dateValues: {},
  }),
  setup() {
    const {t} = useI18n();
    return {t};
  },
  computed: {
    buttonText() {
      const count = this.selectedDates.length;

      if (count === 0) {
        return this.placeholder;
      }

      if (count === 1) {
        return this.selectedDates[0].label;
      }

      return this.t('modals.selected')+`: ${count}`;
    },
    selectedDates() {
      return this.options
          .filter(option => this.dateValues[option.value])
          .map(option => ({
            value: option.value,
            label: option.label,
            date: this.dateValues[option.value],
          }));
    },
  },
  watch: {
    modelValue: {
      handler(newValue) {
        this.dateValues = { ...newValue };
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.onDocumentClick = this.onDocumentClick.bind(this);
    document.addEventListener('click', this.onDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick);
  },
  methods: {
    async toggleDropdown() {
      this.isOpen = !this.isOpen;

      await nextTick();

      const list = this.$refs.dropdownList;
      const inner = this.$refs.dropdownInner;
      if (!list || !inner) return;

      if (this.isOpen) {
        const contentHeight = inner.scrollHeight;

        // Если высота больше 400px, ограничиваем и включаем прокрутку
        if (contentHeight > 400) {
          list.style.maxHeight = '400px';
          list.style.overflowY = 'auto';
          inner.style.height = 'auto';
        } else {
          // Иначе показываем весь контент без прокрутки
          list.style.maxHeight = (contentHeight + 20) + 'px';
          list.style.overflowY = 'hidden';
          inner.style.height = (contentHeight + 20) + 'px';
        }
      } else {
        list.style.maxHeight = '0';
        list.style.overflowY = 'hidden';
        inner.style.height = '0';
      }
    },
    async updateDate(key, value) {
      const nextValue = { ...this.dateValues };

      if (value) {
        nextValue[key] = value;
      } else {
        delete nextValue[key];
      }

      this.dateValues = nextValue;
      this.$emit('update:modelValue', nextValue);

      await nextTick();

      const list = this.$refs.dropdownList;
      const inner = this.$refs.dropdownInner;
      if (list && inner && this.isOpen) {
        const contentHeight = inner.scrollHeight;

        if (contentHeight > 200) {
          list.style.maxHeight = '200px';
          list.style.overflowY = 'auto';
          inner.style.height = 'auto';
        } else {
          list.style.maxHeight = (contentHeight + 20) + 'px';
          list.style.overflowY = 'hidden';
          inner.style.height = (contentHeight + 20) + 'px';
        }
      }
    },
    async removeDate(key) {
      const nextValue = { ...this.dateValues };
      delete nextValue[key];

      this.dateValues = nextValue;
      this.$emit('update:modelValue', nextValue);

      await nextTick();

      const list = this.$refs.dropdownList;
      const inner = this.$refs.dropdownInner;
      if (list && inner && this.isOpen) {
        const contentHeight = inner.scrollHeight;

        if (contentHeight > 400) {
          list.style.maxHeight = '400px';
          list.style.overflowY = 'auto';
          inner.style.height = 'auto';
        } else {
          list.style.maxHeight = (contentHeight + 20) + 'px';
          list.style.overflowY = 'hidden';
          inner.style.height = (contentHeight + 20) + 'px';
        }
      }
    },
    formatDate(date) {
      if (!date) return '';

      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();

      return `${day}.${month}.${year}`;
    },
    onDocumentClick(e) {
      const dropdown = this.$refs.dropdown;
      if (!dropdown) return;

      if (!dropdown.contains(e.target)) {
        this.isOpen = false;

        const list = this.$refs.dropdownList;
        const inner = this.$refs.dropdownInner;
        if (list && inner) {
          list.style.maxHeight = '0';
          list.style.overflowY = 'hidden';
          inner.style.height = '0';
        }
      }
    },
  },
};
</script>